// 📈 B242 MVP2 (AI_112) — GRÁFICO da coorte (SVG puro, sem dependência externa).
// Box plot p/ variável numérica; barras p/ categórica/booleana. Os NÚMEROS vêm da
// camada pura (graficos.ts); aqui é só desenho. Células n<5 são MASCARADAS (não
// se desenha a distribuição de um estrato pequeno — privacidade).
import React from "react";
import { theme } from "../../../shared/styles/theme";
import { RegistroPesquisa, CampoPesquisa } from "../types";
import {
  dadosBoxplot,
  dominioBoxplot,
  dadosBarras,
  rotulosCategorias,
} from "../services/graficos";
import { N_MINIMO_CELULA } from "../services/estatisticasDescritivas";

// Paleta para colunas/estratos (Total + estratos).
const CORES = [
  theme.primary[600],
  "#0d9488",
  "#d97706",
  "#7c3aed",
  "#dc2626",
  "#2563eb",
  "#65a30d",
];

const LARGURA = 620;
const MARG_ESQ = 120;
const MARG_DIR = 70;
const PLOT = LARGURA - MARG_ESQ - MARG_DIR;

function arred(x: number, casas = 1): number {
  const f = Math.pow(10, casas);
  return Math.round(x * f) / f;
}

export default function GraficoCoorte({
  registros,
  campo,
  estratificarPor,
}: {
  registros: RegistroPesquisa[];
  campo: CampoPesquisa;
  estratificarPor?: string;
}) {
  if (campo.tipo === "numerico") {
    return <Boxplot registros={registros} campo={campo} estratificarPor={estratificarPor} />;
  }
  return <Barras registros={registros} campo={campo} estratificarPor={estratificarPor} />;
}

// ── Box plot horizontal (uma linha por coluna) ────────────────────────────────
function Boxplot({
  registros,
  campo,
  estratificarPor,
}: {
  registros: RegistroPesquisa[];
  campo: CampoPesquisa;
  estratificarPor?: string;
}) {
  const colunas = dadosBoxplot(registros, campo.chave, estratificarPor);
  const dominio = dominioBoxplot(colunas);

  if (!dominio) {
    return (
      <p style={{ fontSize: "13px", color: theme.gray[500] }}>
        Dados insuficientes para o gráfico (menos de {N_MINIMO_CELULA} valores).
      </p>
    );
  }

  const { min, max } = dominio;
  const x = (v: number) => MARG_ESQ + ((v - min) / (max - min)) * PLOT;
  const linhaAltura = 46;
  const topo = 16;
  const altura = topo + colunas.length * linhaAltura + 36;
  const unid = campo.unidade ? ` (${campo.unidade})` : "";

  return (
    <svg viewBox={`0 0 ${LARGURA} ${altura}`} width="100%" style={{ maxWidth: LARGURA }}>
      {colunas.map((c, i) => {
        const y = topo + i * linhaAltura + linhaAltura / 2;
        const cor = CORES[i % CORES.length];
        if (c.pequeno || c.n === 0) {
          return (
            <g key={c.coluna}>
              <text x={4} y={y + 4} fontSize={11} fill={theme.gray[600]} fontWeight={600}>
                {c.coluna}
              </text>
              <text x={MARG_ESQ} y={y + 4} fontSize={11} fill={theme.gray[400]}>
                {c.n === 0 ? "sem dados" : `< ${N_MINIMO_CELULA} (mascarado)`}
              </text>
            </g>
          );
        }
        const xmin = x(c.min as number);
        const xmax = x(c.max as number);
        const xq1 = x(c.q1 as number);
        const xq3 = x(c.q3 as number);
        const xmed = x(c.mediana as number);
        return (
          <g key={c.coluna}>
            <text x={4} y={y - 4} fontSize={11} fill={theme.gray[700]} fontWeight={600}>
              {c.coluna}
            </text>
            <text x={4} y={y + 10} fontSize={10} fill={theme.gray[400]}>
              n={c.n}
            </text>
            {/* whisker min–max */}
            <line x1={xmin} y1={y} x2={xmax} y2={y} stroke={cor} strokeWidth={1.5} />
            <line x1={xmin} y1={y - 6} x2={xmin} y2={y + 6} stroke={cor} strokeWidth={1.5} />
            <line x1={xmax} y1={y - 6} x2={xmax} y2={y + 6} stroke={cor} strokeWidth={1.5} />
            {/* box Q1–Q3 */}
            <rect
              x={Math.min(xq1, xq3)}
              y={y - 10}
              width={Math.max(2, Math.abs(xq3 - xq1))}
              height={20}
              fill={cor}
              fillOpacity={0.18}
              stroke={cor}
              strokeWidth={1.5}
            />
            {/* mediana */}
            <line x1={xmed} y1={y - 10} x2={xmed} y2={y + 10} stroke={cor} strokeWidth={2.5} />
          </g>
        );
      })}
      {/* eixo X */}
      <line
        x1={MARG_ESQ}
        y1={altura - 24}
        x2={MARG_ESQ + PLOT}
        y2={altura - 24}
        stroke={theme.gray[300]}
      />
      <text x={MARG_ESQ} y={altura - 8} fontSize={10} fill={theme.gray[500]}>
        {arred(min)}
      </text>
      <text x={MARG_ESQ + PLOT} y={altura - 8} fontSize={10} fill={theme.gray[500]} textAnchor="end">
        {arred(max)}
        {unid}
      </text>
      <text
        x={MARG_ESQ + PLOT / 2}
        y={altura - 8}
        fontSize={10}
        fill={theme.gray[400]}
        textAnchor="middle"
      >
        caixa = Q1–Q3 · traço = mediana · hastes = mín–máx
      </text>
    </svg>
  );
}

// ── Barras agrupadas (categórica/booleana) ────────────────────────────────────
function Barras({
  registros,
  campo,
  estratificarPor,
}: {
  registros: RegistroPesquisa[];
  campo: CampoPesquisa;
  estratificarPor?: string;
}) {
  const colunas = dadosBarras(registros, campo.chave, estratificarPor);
  const categorias = rotulosCategorias(colunas);
  // Rótulo amigável da categoria (usa opções do catálogo quando houver).
  const rotuloCat = (valor: string) =>
    campo.opcoes?.find((o) => o.valor === valor)?.label ?? valor;

  if (categorias.length === 0) {
    return <p style={{ fontSize: "13px", color: theme.gray[500] }}>Sem dados para o gráfico.</p>;
  }

  const estratificado = colunas.length > 1;
  const barH = estratificado ? 13 : 18;
  const gapBarra = 3;
  const gapGrupo = 14;
  const grupoH = colunas.length * (barH + gapBarra) + gapGrupo;
  const topo = estratificado ? 22 : 8;
  const altura = topo + categorias.length * grupoH + 8;

  return (
    <svg viewBox={`0 0 ${LARGURA} ${altura}`} width="100%" style={{ maxWidth: LARGURA }}>
      {/* legenda (só quando estratificado) */}
      {estratificado &&
        colunas.map((c, i) => (
          <g key={`leg-${c.coluna}`} transform={`translate(${MARG_ESQ + i * 120}, 6)`}>
            <rect width={10} height={10} fill={CORES[i % CORES.length]} rx={2} />
            <text x={14} y={9} fontSize={10} fill={theme.gray[600]}>
              {c.coluna}
            </text>
          </g>
        ))}

      {categorias.map((cat, ci) => {
        const grupoY = topo + ci * grupoH;
        return (
          <g key={cat}>
            <text x={4} y={grupoY + grupoH / 2} fontSize={11} fill={theme.gray[700]} fontWeight={600}>
              {rotuloCat(cat)}
            </text>
            {colunas.map((col, coli) => {
              const dado = col.categorias.find((c) => c.categoria === cat);
              const y = grupoY + coli * (barH + gapBarra);
              const cor = CORES[coli % CORES.length];
              const prop = dado ? dado.proporcao : 0;
              const w = Math.max(1, prop * PLOT);
              const mascarar = dado?.pequeno;
              const rotulo = !dado
                ? "0"
                : mascarar
                ? `< ${N_MINIMO_CELULA}`
                : `${dado.n} (${arred(prop * 100, 0)}%)`;
              return (
                <g key={col.coluna}>
                  <rect
                    x={MARG_ESQ}
                    y={y}
                    width={w}
                    height={barH}
                    fill={cor}
                    fillOpacity={mascarar ? 0.25 : 0.85}
                    rx={2}
                  />
                  <text
                    x={MARG_ESQ + w + 5}
                    y={y + barH - 3}
                    fontSize={10}
                    fill={theme.gray[600]}
                  >
                    {rotulo}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}
